import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { migrateGuestData } from "@/lib/cloudServices";
import { getHistoryEntries } from "@/lib/watchlistStore";

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isGuest: boolean;
}

interface AuthContextType extends AuthState {
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  continueAsGuest: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isGuest: false,
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setState((prev) => ({
        user: session?.user ?? null,
        session,
        isLoading: false,
        isGuest: !session ? prev.isGuest : false,
      }));

      // Migrate guest data on first sign-in
      if (event === "SIGNED_IN" && session?.user) {
        const wasGuest = localStorage.getItem("dos_guest") === "true";
        if (wasGuest) {
          const localHistory = getHistoryEntries() as unknown as Record<string, unknown>[];
          const localWatchlist = JSON.parse(localStorage.getItem("decision-os-watchlist") || "[]") as Record<string, unknown>[];
          if (localHistory.length > 0 || localWatchlist.length > 0) {
            migrateGuestData(session.user.id, localHistory, localWatchlist).then((result) => {
              console.log("Guest data migrated:", result);
              // Clear local data after successful migration
              localStorage.removeItem("decision-os-history");
              localStorage.removeItem("decision-os-watchlist");
            });
          }
          localStorage.removeItem("dos_guest");
        }
      }
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const wasGuest = localStorage.getItem("dos_guest") === "true";
      setState({
        user: session?.user ?? null,
        session,
        isLoading: false,
        isGuest: !session && wasGuest,
      });

      // Plan is now read from DB via usePlan() hook — no localStorage sync needed
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    if (!error) localStorage.removeItem("dos_guest");
    return { error: error?.message ?? null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) localStorage.removeItem("dos_guest");
    return { error: error?.message ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem("dos_guest");
    setState({ user: null, session: null, isLoading: false, isGuest: false });
  };

  const continueAsGuest = () => {
    localStorage.setItem("dos_guest", "true");
    setState((prev) => ({ ...prev, isGuest: true }));
  };

  return (
    <AuthContext.Provider value={{ ...state, signUp, signIn, signOut, continueAsGuest }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
